import { createStore } from '@stencil/store'

import { setPreferences } from './app/preferences'
import { setGameState } from './gamification/ui'
import { IUser } from './page/admin/types'

type AnnouncementMessageType =
  | 'invited_to_team'
  | 'delayed_transcripts'
  | 'database_issues'
  | 'team_view_beta'
  | 'bot_failures'
  | 'wefunder_exclusive'
  | 'wefunder_round_2'
  | 'product_hunt_launch_2023'
  | 'wefunder_series_a'
  | 'org_team_visibility_opt_in'
  | 'viewable_team_calls_v2_banner'
  | 'keyword_alerts_deprecation_banner'

type ClickHereMessageType = 'team_view_beta' | 'zoom_essential_apps_promotion' | 'complete_onboarding'

type PromptMessageType =
  | 'enable_auto_recording'
  | 'enable_consent_emails'
  | 'completed_onboarding'
  | 'product_hunt_launch'
  | 'product_hunt_golden_kitty'
  | 'team_edition_trial'
  | 'payment_past_due'
  | 'completed_team_onboarding'

type ScheduleCallMessageType = 'team_view_beta'

type SuccessMessageType =
  | 'became_mayor'
  | 'tagged_call'
  | 'connected_salesforce'
  | 'shared_highlight'
  | 'signed_up_with_your_invite_code'
  | 'connected_hubspot'
  | 'accepted_gift_from_admin'
  | 'completed_test_call'
  | 'connected_closeio'
  | 'completed_tutorial'
  | 'completed_onboarding'
  | 'connected_zoom'

type OverrideMessageType =
  | 'call_access_request'
  | 'premium_plan_user_free_preview'
  | 'premium_plan_user_free_preview_ending'
  | 'premium_plan_user_free_preview_ended'
  | 'advanced_ai_team_free_preview'
  | 'premium_plan_ai_free_preview_summer_2024'
  | 'share_sign_up_prompt'

export interface IBanner {
  id?: number
  type?: 'success' | 'prompt' | 'announcement' | 'schedule_call' | 'click_here' | 'override'
  message: string[]
  messageType?:
    | PromptMessageType
    | SuccessMessageType
    | AnnouncementMessageType
    | ScheduleCallMessageType
    | ClickHereMessageType
    | OverrideMessageType
  autoDismiss: boolean
  learnMoreUrl?: string
  scheduleCallUrl?: string
  clickHereUrl?: string
  metadata?: { [key: string]: any }
  isGamificationOnly?: boolean
}

export interface IGamePoints {
  total?: number
  month?: number
  added?: number
  added_on_page?: boolean
}

export interface ILayoutProps {
  logoutUrl: string
  appDownloadUrl: string
  flash: Array<{ type: 'alert' | 'notice' | 'success' | 'warn'; text: string }>
  showHeader: boolean
  showLoader: boolean
  searchSummaryUrl: string
  banner?: IBanner
  layoutPusherConfig?: Array<{ channel: string; event: string }>
}

export interface IElectronLayoutProps {
  showLoader: boolean
}

export interface IZoomAppLayoutProps {
  showLoader: boolean
}

export type AppPageProps = {
  layout: {
    component: string
    props: ILayoutProps | IElectronLayoutProps | IZoomAppLayoutProps
  }
  env: {
    RAILS_ENV: string
    PUSHER_KEY: string
    PUSHER_CLUSTER: string
    PUSHER_AUTH_URL: string
    APP_VERSION: string
    HOST_NAME: string
  }
  head: {
    title: string
  }
}

export type PusherConfig = { channel: string; event: string } | Array<{ channel: string; event: string }>

export type AppSharedProps = {
  pusherConfig?: PusherConfig
  currentUser?: IUser
  inElectron?: boolean
  electronVersion?: string
  inZapp?: boolean
  adminConfig?: {
    adminPointsUrl: string
    trueUser: IUser
    stopImpersonatingUrl: string
    isImpersonating: boolean
  }
  muxEnvKey?: string

  // URLs from ApplicationController
  appDownloadUrl: string
  customizeUrl: string
  bannerDismissUrl: string
  pointsUrl: string
  liveNotesUrl: string
  homeUrl: string
  logoutUrl: string
  newRelicLogsUrl: string
  playlistsUrl: string
  submitHelpRequestUrl: string
  userTokenUrl: string
  tutorialUrl: string
}

export type SharedProps = AppPageProps & AppSharedProps

export const AppSharedPropsStore = createStore<Partial<SharedProps>>({})

AppSharedPropsStore.onChange('currentUser', (user) => {
  if (user?.preferences) {
    setPreferences(user.preferences)
  }

  if (user?.game_state) {
    setGameState(true, user.game_state.is_mayor, user.game_state.actions)
  } else {
    // If user has no game state, disable gamification
    setGameState(false, false, {})
  }
})
